import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { supportedLanguages } from '@/i18n';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  /**
   * Canonical path WITHOUT the language prefix (e.g. "/pricing", "/blog/my-post").
   * The component will emit a localized canonical for the current language and
   * hreflang alternates for every supported language plus x-default.
   */
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
  structuredData?: object;
}

const BASE_URL = 'https://outsprinter.com';

const OG_LOCALE_MAP: Record<string, string> = {
  en: 'en_US',
  et: 'et_EE',
};

function stripLangPrefix(path: string): string {
  // Strip leading /en or /et if present
  return path.replace(/^\/(en|et)(?=\/|$)/, '') || '/';
}

function getCurrentLang(pathname: string): string {
  const match = pathname.match(/^\/(en|et)(?=\/|$)/);
  return match ? match[1] : 'en';
}

export function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage = 'https://outsprinter.com/og-image.png',
  article,
  structuredData,
}: SEOHeadProps) {
  const location = useLocation();
  const fullTitle = title.includes('Outsprinter') ? title : `${title} | Outsprinter`;

  // Path without language prefix — canonical source of truth
  const cleanPath = canonicalUrl
    ? (canonicalUrl.startsWith('/') ? canonicalUrl : `/${canonicalUrl}`)
    : stripLangPrefix(location.pathname);

  const currentLang = getCurrentLang(location.pathname);
  const localizedCanonical = `${BASE_URL}/${currentLang}${cleanPath === '/' ? '' : cleanPath}`;
  const ogUrl = localizedCanonical;
  const ogLocale = OG_LOCALE_MAP[currentLang] || 'en_US';
  const ogLocaleAlternates = supportedLanguages
    .filter((l) => l !== currentLang)
    .map((l) => OG_LOCALE_MAP[l])
    .filter(Boolean);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={currentLang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={localizedCanonical} />

      {/* hreflang alternates */}
      {supportedLanguages.map((l) => (
        <link
          key={l}
          rel="alternate"
          hrefLang={l}
          href={`${BASE_URL}/${l}${cleanPath === '/' ? '' : cleanPath}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${BASE_URL}/en${cleanPath === '/' ? '' : cleanPath}`} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Outsprinter" />
      <meta property="og:locale" content={ogLocale} />
      {ogLocaleAlternates.map((alt) => (
        <meta key={alt} property="og:locale:alternate" content={alt} />
      ))}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Article specific meta tags */}
      {article && (
        <>
          {article.publishedTime && (
            <meta property="article:published_time" content={article.publishedTime} />
          )}
          {article.modifiedTime && (
            <meta property="article:modified_time" content={article.modifiedTime} />
          )}
          {article.author && <meta property="article:author" content={article.author} />}
          {article.section && <meta property="article:section" content={article.section} />}
          {article.tags?.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      )}
    </Helmet>
  );
}
